import axiosInstance from '@/app/api/axiosInstance';
import { format, startOfMonth } from 'date-fns';
import { PaginatedResponse } from '@/app/types/api';
import { API_BASE_URL } from '@/app/config/api';

export interface DailySummary {
  summary_date: string;
  total_sales: number;
  total_payments_received: number;
  sales_invoice_count: number;
  payment_received_count: number;
}

export interface DashboardStats {
  activeCustomers: number;
  totalItems: number;
  lowStockAlerts: number;
  pendingOrders: number;
  pendingOrdersValue: number;
  monthToDateSales: number;
  monthToDateTarget: number;
}

export interface DashboardDateRangeStats {
  totalSales: number;
  salesCount: number;
  totalPayments: number;
  paymentsCount: number;
  startDate: string;
  endDate: string;
}

export interface DashboardSummary {
  total_incoming: number;
  incoming_count: number;
  total_outgoing: number;
  outgoing_count: number;
  total_receivables: number;
  receivables_count: number;
  total_payables: number;
  payables_count: number;
  start_date: string;
  end_date: string;
}

export interface RecentActivity {
  id: string;
  type: 'sale' | 'payment' | 'order' | 'expense';
  title: string;
  description: string;
  amount?: number;
  time: string;
  user?: {
    name: string;
    avatar?: string;
  };
}

export const dashboardService = {
  async getDashboardSummary(startDate: Date, endDate: Date): Promise<DashboardSummary> {
    const params = {
      start_date: format(startDate, 'yyyy-MM-dd'),
      end_date: format(endDate, 'yyyy-MM-dd'),
    };
    const response = await axiosInstance.get<DashboardSummary>('/reports/dashboard-summary', { params });
    return response.data;
  },

  async getDailySummary(date?: Date): Promise<DailySummary> {
    const params = date ? { summary_date: format(date, 'yyyy-MM-dd') } : {};
    const response = await axiosInstance.get<DailySummary>('/reports/daily-summary', { params });
    return response.data;
  },

  async getDashboardDateRangeStats(startDate: Date, endDate: Date): Promise<DashboardDateRangeStats> {
    try {
      const start = format(startDate, 'yyyy-MM-dd');
      const end = format(endDate, 'yyyy-MM-dd');
      
      // If it's a single day, use the daily summary endpoint which we know works
      if (start === end) {
        const dailySummary = await this.getDailySummary(startDate);
        return {
          totalSales: dailySummary.total_sales,
          salesCount: dailySummary.sales_invoice_count,
          totalPayments: dailySummary.total_payments_received,
          paymentsCount: dailySummary.payment_received_count,
          startDate: start,
          endDate: end
        };
      }
      
      // For date ranges, aggregate daily summaries
      const currentDate = new Date(startDate);
      const endDateObj = new Date(endDate);
      const dailySummaries: DailySummary[] = [];
      
      while (currentDate <= endDateObj) {
        try {
          const summary = await this.getDailySummary(new Date(currentDate));
          dailySummaries.push(summary);
        } catch (error) {
          console.log(`No data for ${format(currentDate, 'yyyy-MM-dd')}`);
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Aggregate the results
      const totalSales = dailySummaries.reduce((sum, day) => sum + day.total_sales, 0);
      const salesCount = dailySummaries.reduce((sum, day) => sum + day.sales_invoice_count, 0);
      const totalPayments = dailySummaries.reduce((sum, day) => sum + day.total_payments_received, 0);
      const paymentsCount = dailySummaries.reduce((sum, day) => sum + day.payment_received_count, 0);

      return {
        totalSales,
        salesCount,
        totalPayments,
        paymentsCount,
        startDate: start,
        endDate: end
      };
    } catch (error: any) {
      console.error('Error fetching dashboard date range stats:', error);
      return {
        totalSales: 0,
        salesCount: 0,
        totalPayments: 0,
        paymentsCount: 0,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      };
    }
  },

  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Get party count first
      let partyCount = 0;
      try {
        const partyResponse = await fetch(`${API_BASE_URL}/parties/?limit=1`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        const partyData = await partyResponse.json();
        partyCount = partyData.total || 0;
      } catch (e) {
        partyCount = 0;
      }

      // Fetch all data in parallel
      const [_, items, lowStock, pendingOrders, monthSales] = await Promise.all([
        Promise.resolve(null), // placeholder for parties since we already fetched it
        
        // Get total items
        axiosInstance.get<PaginatedResponse<any>>('/items/', {
          params: { limit: 1 }
        }),
        
        // Get low stock alerts
        axiosInstance.get<PaginatedResponse<any>>('/reports/low-stock-summary', {
          params: { limit: 1 }
        }),
        
        // Get pending sales orders
        axiosInstance.get<PaginatedResponse<any>>('/documents/', {
          params: { 
            limit: 100,
            filters: JSON.stringify([
              { field: 'document_type', value: 'Sales Order', op: 'eq' },
              { field: 'status', value: 'Pending', op: 'eq' }
            ])
          }
        }),
        
        // Get month to date sales
        this.getMonthToDateSales()
      ]);

      // Calculate pending orders value
      let pendingOrdersValue = 0;
      if (pendingOrders.data.items && pendingOrders.data.items.length > 0) {
        pendingOrdersValue = pendingOrders.data.items.length * 100000; // Rough estimate
      }

      const stats = {
        activeCustomers: partyCount,
        totalItems: items?.data?.total || 0,
        lowStockAlerts: lowStock?.data?.total || 0,
        pendingOrders: pendingOrders?.data?.total || 0,
        pendingOrdersValue,
        monthToDateSales: monthSales,
        monthToDateTarget: 1500000 // Default target, can be made configurable
      };

      console.warn('✅ Dashboard stats calculated:', stats);
      console.warn('🔢 Party count (activeCustomers):', stats.activeCustomers);
      return stats;
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      }
      // Return default values on error
      return {
        activeCustomers: 0,
        totalItems: 0,
        lowStockAlerts: 0,
        pendingOrders: 0,
        pendingOrdersValue: 0,
        monthToDateSales: 0,
        monthToDateTarget: 1500000
      };
    }
  },

  async getMonthToDateSales(): Promise<number> {
    try {
      const now = new Date();
      const monthStart = startOfMonth(now);
      const dateRangeStats = await this.getDashboardDateRangeStats(monthStart, now);
      return dateRangeStats.totalSales;
    } catch (error) {
      console.error('Error fetching month to date sales:', error);
      return 0;
    }
  },

  async getRecentActivities(): Promise<RecentActivity[]> {
    try {
      // Fetch all recent vouchers sorted by creation date
      const response = await axiosInstance.get<PaginatedResponse<any>>('/vouchers/', {
        params: {
          limit: 20,
          sort_by: 'created_at',
          sort_dir: 'desc'
        }
      });

      const activities: RecentActivity[] = [];

      // Process vouchers
      if (response.data.items) {
        response.data.items.forEach((voucher: any) => {
          // Map voucher types to activity types
          let activityType: 'sale' | 'payment' | 'order' | 'expense' = 'sale';
          let title = '';
          
          switch (voucher.voucher_type_id) {
            case 1: // Sales Invoice
              activityType = 'sale';
              title = 'New Sale Invoice';
              break;
            case 3: // Payment Made
            case 4: // Payment Received
              activityType = 'payment';
              title = voucher.voucher_type_id === 4 ? 'Payment Received' : 'Payment Made';
              break;
            case 2: // Purchase Invoice
              activityType = 'expense';
              title = 'Purchase Invoice';
              break;
            default:
              title = voucher.voucher_type?.name || 'Transaction';
          }

          activities.push({
            id: voucher.id.toString(),
            type: activityType,
            title,
            description: `${voucher.voucher_number} - ${voucher.party?.name || 'Unknown'}`,
            amount: parseFloat(voucher.total_amount),
            time: this.getRelativeTime(new Date(voucher.created_at)),
            user: {
              name: voucher.created_by?.name || 'System'
            }
          });
        });
      }

      return activities.slice(0, 10); // Return top 10 activities
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return [];
    }
  },

  getRelativeTime(date: Date): string {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const mins = Math.floor(diffInSeconds / 60);
      return `${mins} min${mins > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  }
};